import type { ReactNode } from "react";
import { AREA_CLS, Collapsible } from "@/shared/ui/Collapsible";
import { formatDateTime } from "@/shared/format";
import { useOrderShipments } from "../../api";
import type { Order, OrderShipment, ShipmentAddress } from "../../types";
import { SkuChip } from "../OrderCard";
import { TrackingCopyPill } from "../SpedizioneCell";
import { PANEL, TITLE, TITLE_NOTE } from "./stile";

type PillTone = "blue" | "green" | "amber" | "ink";

const PILL_TONES: Record<PillTone, string> = {
  blue: "border-blue/30 bg-blue-soft text-ink",
  green: "border-green/30 bg-green-soft text-ink",
  amber: "border-amber/30 bg-amber-soft text-ink",
  ink: "border-line-2 bg-paper-2 text-ink",
};

/** Pill del dossier (`.pill` della spec visiva): 11px, tinta soft del token,
 *  testo che puo' andare a capo. NON e' `StatoBadge`: quella e' la grammatica
 *  della CARD in lista (9.5px uppercase, outline vivido, `shrink-0`) e i suoi
 *  label lunghi qui uscirebbero dalla colonna. Esportata perche' la usa anche
 *  `AddressesSection`. */
export function Pill({ tone, children }: { tone: PillTone; children: ReactNode }) {
  return (
    <span
      className={`inline-flex items-center gap-1.5 rounded-full border px-2 py-[2px] text-[11px] font-semibold leading-[1.6] ${PILL_TONES[tone]}`}
    >
      {children}
    </span>
  );
}

/** Stati spedizione visti live su Poleepo. Uno stato non mappato si mostra
 *  com'e' (tono neutro): meglio un codice grezzo che un'etichetta inventata. */
const STATUS_PILLS: Record<string, { label: string; tone: PillTone }> = {
  DELIVERED: { label: "Consegnato", tone: "green" },
  IN_TRANSIT: { label: "In transito", tone: "amber" },
};

/** "Chi" dell'indirizzo: l'azienda quando c'e' (Itamoby, BS COM), altrimenti
 *  la persona. Esportata: la riusa `AddressesSection`. */
/** Il "chi" di un indirizzo, o `null` se Poleepo non lo dichiara.
 *  ⚠️ Succede DAVVERO ed e' il caso normale, non un dato sporco: su 406646 il
 *  `sender_address` ha company/name/surname tutti `null` e solo la via (e' il
 *  nostro magazzino: Poleepo non ci mette il nome). Rendere "—" sopra un
 *  indirizzo completo si legge come "dato mancante" — quando manca il nome,
 *  l'indirizzo E' l'identita'. Chi rende decide, qui non si inventa un "BS COM"
 *  che l'API non ha mai detto. */
export function addressWho(a: ShipmentAddress): string | null {
  return a.company || [a.name, a.surname].filter(Boolean).join(" ") || null;
}

/** Riga sotto il "chi": via · CAP Citta (PROV) · tel N. Il paese compare solo
 *  se non e' IT (non e' informazione, e' rumore, sul 99% degli ordini). */
export function addressLine(a: ShipmentAddress): string {
  const city = [a.postcode, a.city].filter(Boolean).join(" ");
  const place = city && a.state_iso ? `${city} (${a.state_iso})` : city;
  return [
    a.address,
    place,
    a.country_iso && a.country_iso !== "IT" ? a.country_iso : null,
    a.phone ? `tel ${a.phone}` : null,
  ]
    .filter(Boolean)
    .join(" · ");
}

/** Normalizza chi/via/luogo in una chiave confrontabile. Poleepo non ha un
 *  formato: maiuscole e punteggiatura cambiano tra un record e l'altro.
 *  Esportata perche' il confronto fatturazione↔spedizione di
 *  `AddressesSection` NON parte da uno `ShipmentAddress` ma dai campi
 *  appiattiti di `Order.customer`. */
export function placeKey(
  who: string | null | undefined,
  address: string | null | undefined,
  place: Array<string | null | undefined>,
): string {
  return [who, address, place.filter(Boolean).join(" ")]
    .filter(Boolean)
    .join(" ")
    .toLowerCase()
    .replace(/[^a-z0-9]+/g, " ")
    .trim();
}

/** Chiave di confronto tra due indirizzi di spedizione. Fuori il TELEFONO (il
 *  mittente ce l'ha e il ritiro no, ma il posto e' lo stesso) e fuori il PAESE
 *  (`Order.customer` non sempre lo porta; CAP+comune+provincia identici in due
 *  paesi diversi non esistono). */
export function addressKey(a: ShipmentAddress | null | undefined): string {
  if (!a) return "";
  return placeKey(addressWho(a), a.address, [a.postcode, a.city, a.state_iso]);
}

/** I colli di una spedizione. Il backend normalizza gli alias di Poleepo e
 *  dichiara `weight/length/width/height` — ma resta un payload esterno senza
 *  contratto a monte (`bordero._peso_totale` prova 3 nomi per il peso proprio
 *  perche' varia): il narrowing resta, non e' ridondanza. */
function packagesOf(shipment: OrderShipment): Record<string, unknown>[] {
  const raw: unknown = shipment.packages;
  if (!Array.isArray(raw)) return [];
  return raw.filter((p): p is Record<string, unknown> => typeof p === "object" && p !== null);
}

/** Misura positiva da un valore senza contratto. Lo zero e' "non misurato",
 *  non "pesa zero": trattarlo come misura stamperebbe «0 kg» su un collo. */
function num(v: unknown): number | null {
  const n = typeof v === "number" ? v : typeof v === "string" ? Number(v.replace(",", ".")) : NaN;
  return Number.isFinite(n) && n > 0 ? n : null;
}

function fmtNum(n: number): string {
  return n.toLocaleString("it-IT", { maximumFractionDigits: 1 });
}

/** Chip di un collo: «5,8 kg · 25×25×32». `null` se del collo non sappiamo
 *  nulla — un chip vuoto non e' informazione. */
function packageLabel(p: Record<string, unknown>): string | null {
  const kg = num(p.weight);
  const l = num(p.length);
  const w = num(p.width);
  const h = num(p.height);
  return (
    [kg ? `${fmtNum(kg)} kg` : null, l && w && h ? `${fmtNum(l)}×${fmtNum(w)}×${fmtNum(h)}` : null]
      .filter(Boolean)
      .join(" · ") || null
  );
}

function shipmentWeight(shipment: OrderShipment): number {
  return packagesOf(shipment).reduce((tot, p) => tot + (num(p.weight) ?? 0), 0);
}

function colliLabel(count: number): string {
  return `${count} ${count === 1 ? "collo" : "colli"}`;
}

function Row({ label, children }: { label: string; children: ReactNode }) {
  return (
    <>
      <div className="text-[12px] font-semibold text-ink">{label}</div>
      <div className="min-w-0 break-words text-ink">{children}</div>
    </>
  );
}

/** Indirizzo in colonna: chi in grassetto + riga sotto. `sub` sostituisce la
 *  riga quando c'e' di meglio da dire (es. «stesso indirizzo del ritiro»). */
function AddressValue({ addr, sub }: { addr: ShipmentAddress | null; sub?: string }) {
  if (!addr) return <span className="text-ink">n/d</span>;
  const who = addressWho(addr);
  const line = addressLine(addr);
  // Senza nome l'indirizzo sale in prima riga invece di lasciare un "—" sopra
  // di se': una via completa non e' un dato mancante.
  if (!who) {
    return (
      <>
        <b className="font-semibold">{line || "n/d"}</b>
        {sub && <span className="block text-[11.5px] text-ink">{sub}</span>}
      </>
    );
  }
  return (
    <>
      <b className="font-semibold">{who}</b>
      <span className="block text-[11.5px] text-ink">{sub ?? line}</span>
    </>
  );
}

function ShipmentCard({ shipment }: { shipment: OrderShipment }) {
  const status = shipment.status;
  const statusPill: { label: string; tone: PillTone } | null = status
    ? (STATUS_PILLS[status.toUpperCase()] ?? { label: status, tone: "ink" })
    : null;
  const items = shipment.items ?? [];
  const packages = packagesOf(shipment);
  const kg = shipmentWeight(shipment);
  const sameSender =
    !!shipment.sender_address &&
    !!shipment.pickup_address &&
    addressKey(shipment.sender_address) === addressKey(shipment.pickup_address);

  // Cio' che si legge da CHIUSO: corriere, stato, quanta roba. Il resto
  // (tracking, contenuto, indirizzi) si guarda quando serve — su un ordine con
  // 2+ spedizioni erano due schede intere aperte per sempre.
  const misure = [
    shipment.packages_count === null ? null : colliLabel(shipment.packages_count),
    kg > 0 ? `${fmtNum(kg)} kg` : null,
  ]
    .filter(Boolean)
    .join(" · ");

  return (
    <Collapsible
      id={`spedizione-${shipment.id}`}
      title={`Spedizione #${shipment.id}`}
      summary={
        <span className="flex flex-wrap items-center gap-1.5">
          {shipment.carrier && <Pill tone="blue">{shipment.carrier}</Pill>}
          {statusPill && <Pill tone={statusPill.tone}>{statusPill.label}</Pill>}
          {misure && <span>{misure}</span>}
        </span>
      }
    >
      <div className="-mx-3 -mb-3 grid grid-cols-1 md:grid-cols-2">
        <div className="min-w-0 border-b border-line p-2.5 md:border-b-0 md:border-r">
          <div className="grid grid-cols-[74px_minmax(0,1fr)] gap-x-2 gap-y-[3px] text-[12px]">
            <Row label="Tracking">
              {shipment.tracking_code ? (
                <TrackingCopyPill code={shipment.tracking_code} />
              ) : (
                <span className="text-ink">n/d</span>
              )}
            </Row>
            {shipment.delivery_date && (
              <Row label="Consegnata">{formatDateTime(shipment.delivery_date)}</Row>
            )}
            {shipment.shipping_option && <Row label="Servizio">{shipment.shipping_option}</Row>}
            <Row label="Contiene">
              {items.length === 0 ? (
                <span className="text-ink">n/d</span>
              ) : (
                items.map((it, i) => (
                  <div
                    key={it.order_row_id ?? `${shipment.id}-${i}`}
                    className="flex flex-wrap items-center gap-1.5"
                  >
                    <span>
                      {it.title ?? it.description ?? "riga senza nome"} × {it.quantity}
                    </span>
                    {it.sku && <SkuChip sku={it.sku} />}
                  </div>
                ))
              )}
            </Row>
          </div>
        </div>
        <div className="min-w-0 p-2.5">
          <div className="grid grid-cols-[74px_minmax(0,1fr)] gap-x-2 gap-y-[3px] text-[12px]">
            <Row label="Ritiro">
              <AddressValue addr={shipment.pickup_address} />
            </Row>
            <Row label="Mittente">
              <AddressValue
                addr={shipment.sender_address}
                sub={sameSender ? "stesso indirizzo del ritiro" : undefined}
              />
            </Row>
            {(shipment.packages_count !== null || packages.length > 0) && (
              <Row label="Colli">
                {shipment.packages_count === null ? (
                  <span className="text-ink">n/d</span>
                ) : (
                  colliLabel(shipment.packages_count)
                )}
                {kg > 0 && ` · ${fmtNum(kg)} kg`}
                {packages.length > 0 && (
                  <div className="mt-1.5 flex flex-wrap gap-1">
                    {packages.map((p, i) => {
                      const label = packageLabel(p);
                      return label ? (
                        <span
                          key={`${shipment.id}-p${i}`}
                          className="whitespace-nowrap rounded-sm border border-line-2 bg-paper-2 px-1.5 py-[2px] font-mono text-[10px] text-ink"
                        >
                          {label}
                        </span>
                      ) : null;
                    })}
                  </div>
                )}
              </Row>
            )}
          </div>
        </div>
      </div>
    </Collapsible>
  );
}

/** Le spedizioni REALI dell'ordine, una card per spedizione: corriere, stato,
 *  tracking copiabile, cosa contiene, dove si ritira e chi spedisce.
 *
 *  Si mostra solo sui 29 ordini con 2+ spedizioni (il gate vive in
 *  `useOrderShipments`): sugli altri 4532 `ShippingSection` racconta gia'
 *  tutto e questa sezione duplicherebbe. Hook a vuoto o Poleepo KO → `null`,
 *  la sezione sparisce e il dossier resta (mai un errore in pagina).
 *
 *  ⚠️ Ritiro e mittente sono cose DIVERSE: su 406646 il mittente e' BS COM su
 *  entrambe le spedizioni e a cambiare e' solo il ritiro (fornitore vs
 *  magazzino). Sono «due punti di ritiro», mai «due mittenti». */
export function ShipmentsSection({ order }: { order: Order }) {
  const shipments = useOrderShipments(order);
  if (shipments.length === 0) return null;

  const pickupPoints = new Set(shipments.map((s) => addressKey(s.pickup_address)).filter(Boolean))
    .size;
  // `packages_count: null` = numero SCONOSCIUTO: se manca su una spedizione il
  // totale d'ordine sarebbe una somma parziale spacciata per totale.
  const colliKnown = shipments.every((s) => s.packages_count !== null);
  const colli = shipments.reduce((tot, s) => tot + (s.packages_count ?? 0), 0);
  const kg = shipments.reduce((tot, s) => tot + shipmentWeight(s), 0);
  const meta = [
    `${shipments.length} spedizioni`,
    pickupPoints > 1 ? `${pickupPoints} punti di ritiro` : null,
    colliKnown ? colliLabel(colli) : null,
    kg > 0 ? `${fmtNum(kg)} kg` : null,
  ]
    .filter(Boolean)
    .join(" · ");

  return (
    <section className={`${PANEL} ${AREA_CLS.merce}`}>
      <h3 className={`mb-3 ${TITLE}`}>
        Spedizioni <span className={TITLE_NOTE}>{meta}</span>
      </h3>
      <div className="space-y-2.5">
        {shipments.map((s) => (
          <ShipmentCard key={s.id} shipment={s} />
        ))}
      </div>
    </section>
  );
}
